import React, { useState, useEffect, useMemo } from "react";
import {
  MapPin,
  Building,
  TrendingUp,
  Shield,
  Clock,
  Zap,
  AlertCircle,
  Maximize2,
  Loader2,
} from "lucide-react";
import TimeTracker from "../PropertyValuationTracker";

// Types
interface Location {
  district: string;
  sector: string;
  cell: string;
  village: string;
}

interface LandParcel {
  area: number;
  landUse: string;
  slopeCategory: string;
  accessToRoad: boolean;
}

interface MainBuilding {
  constructedArea: number;
  constructionYear: number;
  numberOfFloors: number;
  constructionQuality: string;
  exteriorFinish: string;
  roofType: string;
  maintenanceCondition: string;
}

interface Amenities {
  hasElectricity: boolean;
  hasWater: boolean;
  hasSewage: boolean;
  hasInternet: boolean;
  hasFence: boolean;
  hasGarden: boolean;
  hasParkingSpace: boolean;
}

interface ValuationDetails {
  status: string;
  landValue: number;
  buildingValue: number;
  amenitiesValue: number;
  fenceLength: number;
  fenceRate?: number;
  verandahRate: number;
  verandahArea: number;
  entranceGateRate: number;
  entranceGateLength: number;
  depreciationValue: number;
  totalPropertyValue: number;
  openMarketValue?: number;
  insuranceValue?: number;
  forcedSaleValue?: number;
  improvementsValue?: number;
  mainHouseRate?: number;
  mainHouseArea?: number;
  annexRate?: number;
  annexHouseArea?: number;
  retainingWallRate?: number;
  retainingWallsLength?: number;
  pavedAreaRate?: number;
  pavedArea?: number;
  septicTankValue?: number;
  septicTankNumber?: number;
  gardenRate?: number;
  gardenArea?: number;
  valuationDate: string;
  collectorNotes: string;
  approvedBy: string | null;
  approvedDate: string | null;
  assignedCollector: string;
}

interface Property {
  id: string;
  country: string;
  propertyId: string;
  upi?: string;
  ownerName: string;
  ownerIdNumber: string;
  phone?: string;
  plotNumber?: string;
  location: Location;
  landParcel?: LandParcel;
  mainBuilding?: MainBuilding;
  amenities?: Amenities;
  buildingComponents?: any;
  valuationDetails?: ValuationDetails;
  adjustmentsAndVariables?: any;
  buildingDescription?: any;
  propertyDetails?: any;
  propertyPhotos?: any;
  verandahRate?: number;
  verandahArea?: number;
  bedrooms?: number;
  bathrooms?: number;
  yearOfConstruction?: number;
  landArea?: number;
  landUse?: string;
  annexHouseArea?: number;
  annexRate?: number;
  fenceLength?: number;
  fenceRate?: number;
  gardenArea?: number;
  gardenRate?: number;
  retainingWallsLength?: number;
  retainingWallRate?: number;
  entranceGateLength?: number;
  entranceGateRate?: number;
  pavedArea?: number;
  pavedAreaRate?: number;
  septicTankNumber?: number;
  septicTankValue?: number;
  mainHouseArea?: number;
  mainHouseRate?: number;
}

interface ValuationCardProps {
  icon: React.ElementType;
  country?: string;
  title: string;
  value: number;
  description: string;
  bgColor: string;
  iconBgColor: string;
  iconColor: string;
  badgeColor: string;
  badgeBgColor: string;
  footer?: {
    text: string;
    color: string;
    icon?: React.ElementType;
  };
}

// Valuation Card Component
const ValuationCard: React.FC<ValuationCardProps> = ({
  icon: Icon,
  country,
  title,
  value,
  description,
  bgColor,
  iconBgColor,
  iconColor,
  badgeColor,
  badgeBgColor,
  footer,
}) => {
  const isUganda =
    country === "UG";
  const currency = isUganda ? "UGX" : "RWF";

  const formatCurrency = (amount: number): string =>
    `${currency} ${Number(amount || 0).toLocaleString()}`;

  return (
    <div className={`${bgColor} rounded-xl shadow-lg p-6`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`${iconBgColor} rounded-full p-3`}>
          <Icon className={`${iconColor} w-6 h-6`} />
        </div>
        <span className={`${badgeBgColor} ${badgeColor} px-3 py-1 rounded-full text-xs font-semibold`}>
          {title}
        </span>
      </div>

      <div className="mb-2">
        <h2 className="text-3xl font-bold text-gray-900">
          {formatCurrency(value)}
        </h2>
      </div>

      <p className="text-sm text-gray-700 leading-relaxed">{description}</p>

      {footer && (
        <div className="mt-4 pt-4 border-t border-gray-300">
          <div className={`flex items-center gap-2 ${footer.color}`}>
            {footer.icon && <footer.icon className="w-4 h-4" />}
            <span className="text-sm font-medium">{footer.text}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Loading Skeleton
const LoadingSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
    <div className="text-center">
      <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
      <p className="text-gray-600 text-lg">Loading property details...</p>
    </div>
  </div>
);

// Error Display
const ErrorDisplay: React.FC<{ message: string; onRetry?: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
    <div className="max-w-md w-full">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center">
          <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Error Loading Property
          </h3>
          <p className="text-gray-600 mb-6">{message}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Main Component
const PropertyValuationDashboard: React.FC<{ propertyId?: string }> = ({
  propertyId = "demo-property-123",
}) => {
  const [property, setProperty] = useState<Property | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const BASE_URL =
    import.meta.env?.VITE_API_BASE_URL ||
    "https://valuate-api.vercel.app/api/v1";

  // Fetch property data
  useEffect(() => {
    const fetchProperty = async () => {
      if (!propertyId) {
        setErrorMessage("No property ID provided");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMessage("");

      try {
        const token = localStorage.getItem("valuation_token");
        const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch property: ${response.statusText}`);
        }

        const result = await response.json();
        setProperty(result.data || result);
      } catch (error) {
        console.error("Error fetching property:", error);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to load property details",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
  }, [propertyId, BASE_URL]);// ── EDIT 1: Currency detection based on property country ──────────────
  const isUganda =
    property?.country === "UG";
  const currency = isUganda ? "UGX" : "RWF";

  const formatCurrency = (amount: number): string =>
    `${currency} ${Number(amount || 0).toLocaleString()}`;



  // Memoized calculations
  const calculations = useMemo(() => {
    if (!property?.valuationDetails) {
      return {
        openMarketValue: 0,
        insuranceValue: 0,
        forcedSaleValue: 0,
        improvementValue: 0,
        landValue: 0,
        buildingValue: 0,
        amenitiesValue: 0,
      };
    }
console.log("Property data for calculations:----", property);
    const vd = property.valuationDetails;

    // Calculate amenities value
    let amenitiesValue = 0;
    if (property.annexHouseArea)
      amenitiesValue += property.annexHouseArea * (property.annexRate || 0);
    if (property.fenceLength)
      amenitiesValue += property.fenceLength * (property.fenceRate || 0);
    if (property.gardenArea)
      amenitiesValue += property.gardenArea * (property.gardenRate || 0);
    if (property.retainingWallsLength)
      amenitiesValue +=
        property.retainingWallsLength * (property.retainingWallRate || 0);
    if (property.entranceGateLength)
      amenitiesValue +=
        property.entranceGateLength * (property.entranceGateRate || 0);
    if (property.pavedArea)
      amenitiesValue += property.pavedArea * (property.pavedAreaRate || 0);
    if (property.septicTankNumber)
      amenitiesValue +=
        property.septicTankNumber * (property.septicTankValue || 0);
    if (property.verandahArea)
      amenitiesValue += property.verandahArea * (property.verandahRate || 0);

 const buildingReplacementCost =
        Number(property.mainHouseArea) * (property?.mainHouseRate || 0);
    const depreciatedValue = buildingReplacementCost + amenitiesValue;
    return {
      openMarketValue: property.valuationDetails.landValue + depreciatedValue || 0,
      insuranceValue: depreciatedValue,
      forcedSaleValue:
        (property.valuationDetails.landValue + depreciatedValue) * 0.7 ||
        0,
      improvementValue: vd.improvementsValue || vd.buildingValue || 0,
      landValue: vd.landValue || 0,
      buildingValue: vd.buildingValue || 0,
      amenitiesValue,
    };
  }, [property]);

  // Render states
  if (isLoading) return <LoadingSkeleton />;
  if (errorMessage)
    return (
      <ErrorDisplay
        message={errorMessage}
        onRetry={() => window.location.reload()}
      />
    );
  if (!property) return <ErrorDisplay message="No property data available" />;

  const valuationCards = [
    {
      icon: TrendingUp,
      country: property?.country,
      title: "Open Market Value",
      value: calculations.openMarketValue,
      description:
        "Estimated amount for which the property should exchange between a willing buyer and seller",
      bgColor: "bg-blue-300",
      iconBgColor: "bg-blue-100",
      iconColor: "text-blue-600",
      badgeColor: "text-blue-600",
      badgeBgColor: "bg-blue-100",
      footer: {
        text: "+0% from last valuation",
        color: "text-green-600",
        icon: Zap,
      },
    },
    {
      icon: Shield,
      country: property?.country,
      title: "Insurance Value",
      value: calculations.insuranceValue,
      description:
        "Value for insurance purposes, typically covering replacement cost",
      bgColor: "bg-emerald-300",
      iconBgColor: "bg-emerald-100",
      iconColor: "text-emerald-600",
      badgeColor: "text-emerald-600",
      badgeBgColor: "bg-emerald-100",
      footer: {
        text: "±5% margin of error",
        color: "text-gray-600",
        icon: AlertCircle,
      },
    },
    {
      icon: Clock,
      country: property?.country,
      title: "Forced Sale Value",
      value: calculations.forcedSaleValue,
      description: "Estimated realization price in forced sale circumstances",
      bgColor: "bg-rose-300",
      iconBgColor: "bg-amber-100",
      iconColor: "text-red-600",
      badgeColor: "text-red-600",
      badgeBgColor: "bg-amber-50",
      footer: {
        text: "70% of market value",
        color: "text-red-600",
      },
    },
    {
      icon: MapPin,
      country: property?.country,
      title: "Land Value",
      value: calculations.landValue,
      description: "Value of the land based on area and location",
      bgColor: "bg-yellow-300",
      iconBgColor: "bg-yellow-100",
      iconColor: "text-yellow-600",
      badgeColor: "text-yellow-700",
      badgeBgColor: "bg-yellow-100",
    },
    {
      icon: Building,
      country: property?.country,
      title: "Building Cost",
      value:
        Number(property.mainHouseArea || 0) *
        (property.mainHouseRate || 0),
      description: "Estimated cost to replace a property as new",
      bgColor: "bg-orange-300",
      iconBgColor: "bg-orange-100",
      iconColor: "text-orange-600",
      badgeColor: "text-orange-700",
      badgeBgColor: "bg-orange-100",
    },
    {
      icon: Maximize2,
      country: property?.country,
      title: "Improvement Value",
      value: calculations.amenitiesValue,
      description: "Total value of all buildings and improvements",
      bgColor: "bg-green-300",
      iconBgColor: "bg-green-100",
      iconColor: "text-green-600",
      badgeColor: "text-green-700",
      badgeBgColor: "bg-green-100",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <TimeTracker propertyId={propertyId} />

        {/* Valuation Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {valuationCards.map((card, index) => (
            <ValuationCard key={index} {...card} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PropertyValuationDashboard;